import { useState, useMemo, useEffect } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { SiteHeader } from "@/components/site-header"
import { authorizedFetch } from "@/lib/api"
import { API_URL } from "@/lib/mutation-utils"
import { usePermissions } from "@/hooks/use-permissions"
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table"
import {
  Dialog, DialogClose, DialogContent, DialogDescription,
  DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog"
import {
  Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle,
} from "@/components/ui/sheet"
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select"
import { SearchableSelect } from "@/components/ui/searchable-select"
import { Pagination } from "@/components/ui/pagination"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  SearchIcon, PlusIcon, PencilIcon, Trash2Icon,
  RotateCcwIcon as RestoreIcon, MapPinIcon, CopyIcon,
} from "lucide-react"
import { cn } from "@/lib/utils"

// ─── Types ────────────────────────────────────────────────────────────────────

type WorkLine = "nacional" | "internacional" | "batea"

const ALL_WORK_LINES: WorkLine[] = ["nacional", "internacional", "batea"]

const WORK_LINE_LABELS: Record<WorkLine, string> = {
  nacional:      "Nacional",
  internacional: "Internacional",
  batea:         "Batea",
}

const WORK_LINE_COLORS: Record<WorkLine, string> = {
  nacional:      "bg-blue-100 text-blue-700",
  internacional: "bg-purple-100 text-purple-700",
  batea:         "bg-amber-100 text-amber-700",
}

interface PartyRow {
  id:          string
  name:        string
  externalId:  string | null
  address:     string | null
  googleMapsUrl: string | null
  cityId:      string | null
  cityName:    string | null
  countryId:   string | null
  countryName: string | null
  latitude:    string | null
  longitude:   string | null
  workLines:   WorkLine[] | null
  deletedAt:   string | null
  createdAt:   string
  updatedAt:   string
}

interface CityOption    { id: string; name: string; code: string; countryId: string }
interface CountryOption { id: string; name: string; isoCode: string }
interface PartiesResponse {
  data: PartyRow[]
  total: number
  page: number
  pageSize: number
}

type FilterStatus = "active" | "deleted" | "all"

const EMPTY_FORM = {
  name:          "",
  externalId:    "",
  address:       "",
  googleMapsUrl: "",
  cityId:        "",
  countryId: "",
  latitude:  "",
  longitude: "",
  workLines: [] as WorkLine[],
}
type FormState = typeof EMPTY_FORM

const PAGE_SIZE = 25

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function PartiesPage() {
  const { can } = usePermissions()
  const qc      = useQueryClient()

  const [search,       setSearch]       = useState("")
  const [page,         setPage]         = useState(1)
  const [filterStatus, setFilterStatus] = useState<FilterStatus>("active")
  const [sheetOpen,    setSheetOpen]    = useState(false)
  const [editing,      setEditing]      = useState<PartyRow | null>(null)
  const [form,         setForm]         = useState<FormState>({ ...EMPTY_FORM })
  const [formError,    setFormError]    = useState<string | null>(null)
  const [deleteOpen,   setDeleteOpen]   = useState(false)
  const [deleting,     setDeleting]     = useState<PartyRow | null>(null)
  const [deleteError,  setDeleteError]  = useState<string | null>(null)
  const [restoringId,  setRestoringId]  = useState<string | null>(null)

  useEffect(() => { setPage(1) }, [search, filterStatus])

  // ── Queries ───────────────────────────────────────────────────────────────

  const { data: partiesResponse, isLoading } = useQuery<PartiesResponse>({
    queryKey: ["parties", { status: filterStatus, search, page }],
    queryFn:  async () => {
      const params = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) })
      if (filterStatus === "active")  params.set("deleted", "false")
      if (filterStatus === "deleted") params.set("deleted", "true")
      if (search.trim())              params.set("search", search.trim())
      const res = await authorizedFetch(`${API_URL}/api/parties?${params}`)
      return res.json() as Promise<PartiesResponse>
    },
  })

  const partiesData = partiesResponse?.data ?? []
  const total = partiesResponse?.total ?? 0

  const { data: cityOptions = [] } = useQuery({
    queryKey: ["cities-select"],
    queryFn:  async () => {
      const res = await authorizedFetch(`${API_URL}/api/geo/cities?deleted=false&type=city`)
      return res.ok ? (res.json() as Promise<CityOption[]>) : []
    },
    enabled:   sheetOpen,
    staleTime: 60_000,
  })

  const { data: countryOptions = [] } = useQuery({
    queryKey: ["countries-select"],
    queryFn:  async () => {
      const res = await authorizedFetch(`${API_URL}/api/geo/countries`)
      return res.ok ? (res.json() as Promise<CountryOption[]>) : []
    },
    enabled:   sheetOpen,
    staleTime: 5 * 60_000,
  })

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  // ── Select options ────────────────────────────────────────────────────────

  // Filter cities by the selected country (if any)
  const filteredCityOptions = useMemo(() => {
    if (!form.countryId) return cityOptions
    return cityOptions.filter(c => c.countryId === form.countryId)
  }, [cityOptions, form.countryId])

  const citySelectOptions = useMemo(() => [
    { value: "__none__", label: "Sin ciudad" },
    ...filteredCityOptions.map(c => ({ value: c.id, label: c.name, sublabel: c.code })),
  ], [filteredCityOptions])

  const countrySelectOptions = useMemo(() => [
    { value: "__none__", label: "Sin país" },
    ...countryOptions.map(c => ({ value: c.id, label: c.name, sublabel: c.isoCode })),
  ], [countryOptions])

  // ── Mutations ─────────────────────────────────────────────────────────────

  function invalidate() {
    qc.invalidateQueries({ queryKey: ["parties"] })
    qc.invalidateQueries({ queryKey: ["parties-select"] })
  }

  function buildPayload(f: FormState) {
    const lat = f.latitude.trim()  !== "" ? parseFloat(f.latitude)  : null
    const lng = f.longitude.trim() !== "" ? parseFloat(f.longitude) : null
    return {
      name:       f.name.trim(),
      externalId: f.externalId.trim() || null,
      address:    f.address.trim()       || null,
      googleMapsUrl: f.googleMapsUrl.trim() || null,
      cityId:        f.cityId              || null,
      countryId: f.countryId || null,
      latitude:  lat != null && !isNaN(lat) ? lat : null,
      longitude: lng != null && !isNaN(lng) ? lng : null,
      workLines: f.workLines.length > 0 ? f.workLines : null,
    }
  }

  const createMut = useMutation({
    mutationFn: async (f: FormState) => {
      const res = await authorizedFetch(`${API_URL}/api/parties`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(buildPayload(f)),
      })
      if (!res.ok) {
        const body = await res.json() as { error?: string }
        throw new Error(body.error ?? "Error al crear")
      }
      return res.json()
    },
    onSuccess: () => { invalidate(); toast.success("Parte creada"); closeSheet() },
    onError:   (err: Error) => setFormError(err.message),
  })

  const updateMut = useMutation({
    mutationFn: async (f: FormState) => {
      if (!editing) throw new Error("Sin parte seleccionada")
      const res = await authorizedFetch(`${API_URL}/api/parties/${editing.id}`, {
        method:  "PATCH",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(buildPayload(f)),
      })
      if (!res.ok) {
        const body = await res.json() as { error?: string }
        throw new Error(body.error ?? "Error al actualizar")
      }
      return res.json()
    },
    onSuccess: () => { invalidate(); toast.success("Parte actualizada"); closeSheet() },
    onError:   (err: Error) => setFormError(err.message),
  })

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      const res = await authorizedFetch(`${API_URL}/api/parties/${id}`, { method: "DELETE" })
      if (!res.ok) {
        const body = await res.json() as { error?: string }
        throw new Error(body.error ?? "Error al eliminar")
      }
    },
    onSuccess: () => {
      invalidate()
      toast.success("Parte eliminada")
      setDeleteOpen(false)
      setDeleting(null)
      setDeleteError(null)
    },
    onError: (err: Error) => setDeleteError(err.message),
  })

  const restoreMut = useMutation({
    mutationFn: async (id: string) => {
      setRestoringId(id)
      try {
        const res = await authorizedFetch(`${API_URL}/api/parties/${id}/restore`, { method: "PATCH" })
        if (!res.ok) {
          const body = await res.json() as { error?: string }
          throw new Error(body.error ?? "Error al restaurar")
        }
      } finally {
        setRestoringId(null)
      }
    },
    onSuccess: () => { invalidate(); toast.success("Parte restaurada") },
    onError:   (err: Error) => toast.error(err.message),
  })

  // ── Helpers ───────────────────────────────────────────────────────────────

  function closeSheet() {
    setSheetOpen(false)
    setEditing(null)
    setForm({ ...EMPTY_FORM })
    setFormError(null)
  }

  function openCreate() {
    setEditing(null)
    setForm({ ...EMPTY_FORM })
    setFormError(null)
    setSheetOpen(true)
  }

  function openEdit(p: PartyRow) {
    setEditing(p)
    setForm({
      name:       p.name,
      externalId: p.externalId  ?? "",
      address:    p.address       ?? "",
      googleMapsUrl: p.googleMapsUrl ?? "",
      cityId:        p.cityId        ?? "",
      countryId: p.countryId ?? "",
      latitude:  p.latitude  ?? "",
      longitude: p.longitude ?? "",
      workLines: p.workLines ?? [],
    })
    setFormError(null)
    setSheetOpen(true)
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setFormError(null)
    if (!form.name.trim()) {
      setFormError("El nombre es obligatorio")
      return
    }
    if (editing) updateMut.mutate(form)
    else         createMut.mutate(form)
  }

  const isSaving = createMut.isPending || updateMut.isPending

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <SiteHeader title="Partes" />

      <div className="flex flex-1 flex-col px-4 py-6 lg:px-6 space-y-4">

        {/* Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative w-72">
              <SearchIcon className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nombre, ID externo, ciudad o país..."
                className="pl-8"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1) }}
              />
            </div>
            <Select
              value={filterStatus}
              onValueChange={(v) => { setFilterStatus(v as FilterStatus); setPage(1) }}
            >
              <SelectTrigger className="w-[130px]">
                <SelectValue>
                  {filterStatus === "active"  ? "Activos"
                   : filterStatus === "deleted" ? "Eliminados"
                   : "Todos"}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Activos</SelectItem>
                <SelectItem value="deleted">Eliminados</SelectItem>
                <SelectItem value="all">Todos</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button onClick={openCreate} className="gap-2">
            <PlusIcon className="size-4" /> Nueva parte
          </Button>
        </div>

        {/* Table */}
        <div className="rounded-md border bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead className="w-28">ID externo</TableHead>
                <TableHead className="w-40">Ciudad</TableHead>
                <TableHead className="w-28">Líneas</TableHead>
                <TableHead className="w-12 text-center">Mapa</TableHead>
                {can("write", "parties") && <TableHead className="w-24 text-right pr-4">Acciones</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={can("write", "parties") ? 6 : 5} className="h-24 text-center text-muted-foreground">
                    Cargando partes...
                  </TableCell>
                </TableRow>
              ) : partiesData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={can("write", "parties") ? 6 : 5} className="h-24 text-center text-muted-foreground">
                    No se encontraron partes.
                  </TableCell>
                </TableRow>
              ) : partiesData.map((p) => (
                <TableRow key={p.id} className={cn(p.deletedAt && "opacity-50 italic")}>
                  <TableCell className="font-medium text-sm">
                      <div className="flex items-center gap-1 group">
                        <span>{p.name}</span>
                        <button
                          type="button"
                          title="Copiar ID"
                          onClick={() => {
                            navigator.clipboard.writeText(p.id)
                            toast.success("ID copiado", { description: p.id, duration: 2000 })
                          }}
                          className="text-muted-foreground/40 hover:text-muted-foreground transition-colors opacity-0 group-hover:opacity-100"
                        >
                          <CopyIcon className="size-2.5" />
                        </button>
                      </div>
                    </TableCell>
                  <TableCell className="font-mono text-sm text-muted-foreground">
                    {p.externalId ?? <span className="text-muted-foreground/30">—</span>}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {p.cityName ?? <span className="text-muted-foreground/30">—</span>}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-0.5">
                      {p.workLines && p.workLines.length > 0
                        ? p.workLines.map(wl => (
                            <span key={wl} className={cn("text-[10px] px-1.5 py-0 rounded-full border", WORK_LINE_COLORS[wl])}>
                              {WORK_LINE_LABELS[wl]}
                            </span>
                          ))
                        : <span className="text-muted-foreground/30">—</span>
                      }
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    {p.googleMapsUrl || (p.latitude && p.longitude) ? (
                      <a
                        href={p.googleMapsUrl || `https://www.google.com/maps/search/?api=1&query=${p.latitude},${p.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center text-blue-500 hover:text-blue-700"
                        title="Abrir en Google Maps"
                      >
                        <MapPinIcon className="size-4" />
                      </a>
                    ) : (
                      <span className="text-muted-foreground/30">—</span>
                    )}
                  </TableCell>
                  {can("write", "parties") && (
                    <TableCell className="text-right pr-4">
                      <div className="flex justify-end gap-1">
                        {p.deletedAt ? (
                          <Button
                            variant="ghost" size="icon-sm"
                            onClick={() => restoreMut.mutate(p.id)}
                            disabled={restoringId === p.id}
                            className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                            title="Restaurar"
                          >
                            <RestoreIcon className="size-3.5" />
                          </Button>
                        ) : (
                          <>
                            <Button
                              variant="ghost" size="icon-sm"
                              onClick={() => openEdit(p)}
                              title="Editar"
                            >
                              <PencilIcon className="size-3.5" />
                            </Button>
                            <Button
                              variant="ghost" size="icon-sm"
                              onClick={() => { setDeleting(p); setDeleteOpen(true) }}
                              className="text-destructive hover:text-destructive hover:bg-destructive/10"
                              title="Eliminar"
                            >
                              <Trash2Icon className="size-3.5" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={total}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
          />
        </div>
      </div>

      {/* ── Create / Edit sheet ── */}
      <Sheet open={sheetOpen} onOpenChange={(open) => { if (!open) closeSheet() }}>
        <SheetContent side="right" className="sm:max-w-md overflow-y-auto flex flex-col">
          <SheetHeader>
            <SheetTitle>{editing ? "Editar parte" : "Nueva parte"}</SheetTitle>
          </SheetHeader>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4 px-4 pb-4 flex-1">

            {/* Nombre */}
            <div className="grid gap-1.5">
              <Label htmlFor="p-name">
                Nombre <span className="text-destructive">*</span>
              </Label>
              <Input
                id="p-name"
                value={form.name}
                onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                maxLength={250}
                placeholder="Ej: NESTLE BRASIL LTDA."
                required
              />
            </div>

            {/* ID externo */}
            <div className="grid gap-1.5">
              <Label htmlFor="p-external-id">ID externo</Label>
              <Input
                id="p-external-id"
                value={form.externalId}
                onChange={(e) => setForm(f => ({ ...f, externalId: e.target.value }))}
                maxLength={50}
                placeholder="Ej: 1005"
              />
              <span className="text-xs text-muted-foreground">
                Identificador de la parte en el sistema del cliente.
              </span>
            </div>

            {/* País */}
            <div className="grid gap-1.5">
              <Label>País</Label>
              <SearchableSelect
                options={countrySelectOptions}
                value={form.countryId || "__none__"}
                onValueChange={(v) => {
                  const newCountryId = v === "__none__" ? "" : v
                  setForm(f => {
                    // Clear city if it doesn't belong to the newly selected country
                    const cityStillValid = newCountryId === ""
                      ? true
                      : cityOptions.find(c => c.id === f.cityId)?.countryId === newCountryId
                    return {
                      ...f,
                      countryId: newCountryId,
                      cityId: cityStillValid ? f.cityId : "",
                    }
                  })
                }}
                placeholder="Sin país"
                searchPlaceholder="Buscar país..."
              />
            </div>

            {/* Ciudad */}
            <div className="grid gap-1.5">
              <Label>Ciudad</Label>
              <SearchableSelect
                options={citySelectOptions}
                value={form.cityId || "__none__"}
                onValueChange={(v) => {
                  const newCityId = v === "__none__" ? "" : v
                  const city = cityOptions.find(c => c.id === newCityId)
                  setForm(f => ({
                    ...f,
                    cityId: newCityId,
                    // Auto-fill country from selected city if country is not set
                    countryId: f.countryId || (city?.countryId ?? ""),
                  }))
                }}
                placeholder="Sin ciudad"
                searchPlaceholder="Buscar ciudad..."
              />
            </div>

            {/* Dirección */}
            <div className="grid gap-1.5">
              <Label htmlFor="p-address">Dirección</Label>
              <Input
                id="p-address"
                value={form.address}
                onChange={(e) => setForm(f => ({ ...f, address: e.target.value }))}
                maxLength={500}
                placeholder="Dirección completa..."
              />
            </div>

            {/* Google Maps link */}
            <div className="grid gap-1.5">
              <Label htmlFor="p-google-maps-url">Link de Google Maps</Label>
              <Input
                id="p-google-maps-url"
                type="url"
                value={form.googleMapsUrl}
                onChange={(e) => setForm(f => ({ ...f, googleMapsUrl: e.target.value }))}
                maxLength={2048}
                placeholder="https://maps.app.goo.gl/..."
              />
              <span className="text-xs text-muted-foreground">
                Pegá el enlace del punto compartido desde Google Maps.
              </span>
              {form.googleMapsUrl.trim() && (
                <a
                  href={form.googleMapsUrl.trim()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-xs text-blue-500 hover:text-blue-700"
                >
                  <MapPinIcon className="size-3" />
                  Probar enlace
                </a>
              )}
            </div>

            {/* Líneas de trabajo */}
            <div className="grid gap-1.5">
              <Label>Líneas de trabajo</Label>
              <div className="flex flex-col gap-2">
                {ALL_WORK_LINES.map((wl) => (
                  <label key={wl} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.workLines.includes(wl)}
                      onChange={(e) => setForm(f => ({
                        ...f,
                        workLines: e.target.checked
                          ? [...f.workLines, wl]
                          : f.workLines.filter(w => w !== wl),
                      }))}
                      className="h-4 w-4 rounded border-input accent-foreground cursor-pointer"
                    />
                    <span className={cn("text-[11px] font-medium px-2 py-0.5 rounded-full border", WORK_LINE_COLORS[wl])}>
                      {WORK_LINE_LABELS[wl]}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Coordenadas */}
            <div className="grid gap-1.5">
              <Label>Ubicación GPS</Label>
              <div className="grid grid-cols-2 gap-2">
                <div className="grid gap-1">
                  <span className="text-xs text-muted-foreground">Latitud</span>
                  <Input
                    id="p-lat"
                    type="number"
                    step="any"
                    min="-90"
                    max="90"
                    value={form.latitude}
                    onChange={(e) => setForm(f => ({ ...f, latitude: e.target.value }))}
                    placeholder="-38.375..."
                  />
                </div>
                <div className="grid gap-1">
                  <span className="text-xs text-muted-foreground">Longitud</span>
                  <Input
                    id="p-lng"
                    type="number"
                    step="any"
                    min="-180"
                    max="180"
                    value={form.longitude}
                    onChange={(e) => setForm(f => ({ ...f, longitude: e.target.value }))}
                    placeholder="-68.621..."
                  />
                </div>
              </div>
              {form.latitude && form.longitude && (
                <a
                  href={`https://maps.google.com/?q=${form.latitude},${form.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-xs text-blue-500 hover:text-blue-700 mt-0.5"
                >
                  <MapPinIcon className="size-3" />
                  Ver en Google Maps
                </a>
              )}
            </div>

            {formError && (
              <p className="text-sm text-destructive bg-destructive/10 rounded px-3 py-2">
                {formError}
              </p>
            )}

            <SheetFooter className="px-0 mt-auto pt-4">
              <Button type="button" variant="outline" onClick={closeSheet}>
                Cancelar
              </Button>
              <Button type="submit" disabled={isSaving}>
                {isSaving ? "Guardando..." : editing ? "Guardar cambios" : "Crear parte"}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      {/* ── Delete dialog ── */}
      <Dialog
        open={deleteOpen}
        onOpenChange={(open) => {
          setDeleteOpen(open)
          if (!open) { setDeleting(null); setDeleteError(null) }
        }}
      >
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Eliminar parte</DialogTitle>
            <DialogDescription>
              ¿Eliminás <strong>{deleting?.name}</strong>? Esta acción es reversible.
            </DialogDescription>
          </DialogHeader>

          {deleteError && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2.5 text-sm text-destructive">
              {deleteError}
            </div>
          )}

          <DialogFooter>
            <DialogClose>
              <Button variant="outline">Cancelar</Button>
            </DialogClose>
            <Button
              variant="destructive"
              disabled={deleteMut.isPending || !!deleteError}
              onClick={() => deleting && deleteMut.mutate(deleting.id)}
            >
              {deleteMut.isPending ? "Eliminando..." : "Eliminar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
